import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { AuthService } from '../../../../services/auth.service';
import { ToastService } from '../../../../services/toast.service';
import { UserService } from '../../../../services/user.service';
import { User } from '../../../../models/user.model';
import {
  MORNING_OVERTIME_COMMENT_MAX_LENGTH,
  MORNING_OVERTIME_ERROR_CODES,
  MORNING_OVERTIME_HIDDEN_PROMPT_STATE,
  MORNING_OVERTIME_I18N_KEYS,
  MORNING_OVERTIME_ROLE_HEAD,
  MORNING_OVERTIME_TESTING_ROUTE
} from '../constants/morning-overtime-readiness.constants';
import {
  MorningOvertimeReadinessErrorBody,
  MorningOvertimeReadinessPromptAnswer,
  MorningOvertimeReadinessPromptViewState,
  MorningOvertimeReadinessState,
  MorningOvertimeReadinessSubmitRequest
} from '../models/morning-overtime-readiness.model';
import { MorningOvertimeReadinessApiService } from './morning-overtime-readiness-api.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class MorningOvertimeReadinessFacadeService {
  private promptStateSubject: BehaviorSubject<MorningOvertimeReadinessPromptViewState>;
  private cutoffTimer: number | null;
  private promptStateRequestId: number;

  constructor(
    private apiService: MorningOvertimeReadinessApiService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private toastService: ToastService,
    private translate: TranslateService
  ) {
    this.cutoffTimer = null;
    this.promptStateRequestId = 0;
    this.promptStateSubject = new BehaviorSubject<MorningOvertimeReadinessPromptViewState>(
      MORNING_OVERTIME_HIDDEN_PROMPT_STATE
    );
  }

  public get promptState$(): Observable<MorningOvertimeReadinessPromptViewState> {
    return this.promptStateSubject.asObservable();
  }

  public refreshPrompt(): void {
    if (!this.canRequestState()) {
      this.hidePrompt();
      return;
    }
    var requestId = this.nextPromptStateRequestId();
    this.setLoading();
    this.apiService.getPromptState().subscribe(
      (state: MorningOvertimeReadinessState) => this.handlePromptStateSuccess(state, requestId),
      () => this.handlePromptStateError(requestId)
    );
  }

  public submitPromptAnswer(answer: MorningOvertimeReadinessPromptAnswer): void {
    var request = this.createSubmitRequest(answer);
    if (this.hasCommentValidationError(request.comment)) {
      this.setCommentError();
      return;
    }
    this.setSubmitting();
    this.apiService.submitAnswer(request).subscribe(
      () => this.handleSubmitSuccess(),
      (error: HttpErrorResponse) => this.handleSubmitError(error)
    );
  }

  public clearCutoffTimer(): void {
    if (!this.cutoffTimer) {
      return;
    }
    window.clearTimeout(this.cutoffTimer);
    this.cutoffTimer = null;
  }

  private nextPromptStateRequestId(): number {
    this.promptStateRequestId += 1;
    return this.promptStateRequestId;
  }

  private isCurrentPromptStateRequest(requestId: number): boolean {
    return this.promptStateRequestId === requestId;
  }

  private canRequestState(): boolean {
    var user = this.readUser();
    if (!this.authService.isAuthenticated() || !user) {
      return false;
    }
    if (this.isTestingRoute() || user.testing) {
      return false;
    }
    return true;
  }

  private readUser(): User {
    return this.userService.getLocal();
  }

  private isTestingRoute(): boolean {
    return this.router.url.indexOf(MORNING_OVERTIME_TESTING_ROUTE) === 0;
  }

  private setLoading(): void {
    this.clearCutoffTimer();
    this.promptStateSubject.next({
      isVisible: false,
      isLoading: true,
      isSubmitting: false,
      prompt: null,
      messageKey: '',
      commentErrorKey: ''
    });
  }

  private handlePromptStateSuccess(state: MorningOvertimeReadinessState, requestId: number): void {
    if (!this.isCurrentPromptStateRequest(requestId)) {
      return;
    }
    if (!this.canRequestState()) {
      this.hidePrompt();
      return;
    }
    this.applyBackendState(state);
  }

  private handlePromptStateError(requestId: number): void {
    if (!this.isCurrentPromptStateRequest(requestId)) {
      return;
    }
    this.hidePrompt();
  }

  private applyBackendState(state: MorningOvertimeReadinessState): void {
    if (!state || !state.showPrompt || this.hasHeadRole()) {
      this.hidePrompt();
      return;
    }
    this.promptStateSubject.next({
      isVisible: true,
      isLoading: false,
      isSubmitting: false,
      prompt: state,
      messageKey: '',
      commentErrorKey: ''
    });
    this.scheduleCutoffHide(state);
  }

  private hasHeadRole(): boolean {
    var user = this.readUser();
    if (!user || !user.roles) {
      return false;
    }
    return user.roles.indexOf(MORNING_OVERTIME_ROLE_HEAD) !== -1;
  }

  private scheduleCutoffHide(state: MorningOvertimeReadinessState): void {
    var timeout = this.getCutoffTimeout(state);
    this.clearCutoffTimer();
    if (timeout <= 0) {
      this.hidePrompt();
      return;
    }
    this.cutoffTimer = window.setTimeout(() => this.hidePrompt(), timeout);
  }

  private getCutoffTimeout(state: MorningOvertimeReadinessState): number {
    var cutoffAt = Date.parse(state.cutoffAt);
    var serverNow = Date.parse(state.serverNow);
    if (isNaN(cutoffAt) || isNaN(serverNow)) {
      return 0;
    }
    return cutoffAt - serverNow;
  }

  private createSubmitRequest(
    answer: MorningOvertimeReadinessPromptAnswer
  ): MorningOvertimeReadinessSubmitRequest {
    return {
      ready: answer.ready,
      comment: this.normalizeComment(answer.comment)
    };
  }

  private normalizeComment(comment: string): string | null {
    if (!comment) {
      return null;
    }
    var trimmed = comment.trim();
    return trimmed.length ? trimmed : null;
  }

  private hasCommentValidationError(comment: string | null): boolean {
    return !!comment && comment.length > MORNING_OVERTIME_COMMENT_MAX_LENGTH;
  }

  private setCommentError(): void {
    var state = this.promptStateSubject.getValue();
    this.promptStateSubject.next(this.copyStateWithCommentError(state));
  }

  private copyStateWithCommentError(
    state: MorningOvertimeReadinessPromptViewState
  ): MorningOvertimeReadinessPromptViewState {
    return {
      isVisible: state.isVisible,
      isLoading: false,
      isSubmitting: false,
      prompt: state.prompt,
      messageKey: state.messageKey,
      commentErrorKey: MORNING_OVERTIME_I18N_KEYS.commentTooLong
    };
  }

  private setSubmitting(): void {
    var state = this.promptStateSubject.getValue();
    this.promptStateSubject.next({
      isVisible: state.isVisible,
      isLoading: false,
      isSubmitting: true,
      prompt: state.prompt,
      messageKey: '',
      commentErrorKey: ''
    });
  }

  private handleSubmitSuccess(): void {
    this.hidePrompt();
    this.toastService.success(this.translate.instant(MORNING_OVERTIME_I18N_KEYS.submitSuccess));
  }

  private handleSubmitError(error: HttpErrorResponse): void {
    var body = this.readErrorBody(error);
    if (this.shouldHideAfterSubmitError(body)) {
      this.hidePrompt();
      this.showSubmitInfo(body);
      if (this.shouldRefreshStateAfterSubmitError(body)) {
        this.refreshPrompt();
      }
      return;
    }
    this.showSubmitError(body);
  }

  private readErrorBody(error: HttpErrorResponse): MorningOvertimeReadinessErrorBody | null {
    if (!error || !error.error) {
      return null;
    }
    return error.error as MorningOvertimeReadinessErrorBody;
  }

  private shouldHideAfterSubmitError(body: MorningOvertimeReadinessErrorBody | null): boolean {
    if (!body) {
      return false;
    }
    return body.errorCode === MORNING_OVERTIME_ERROR_CODES.alreadyAnswered ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.afterCutoff ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.groupDisabled ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.dailyTestingRequired ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.roleHead ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.roleNotEligible ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.noMainGroup ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.userNotActive;
  }

  private shouldRefreshStateAfterSubmitError(body: MorningOvertimeReadinessErrorBody | null): boolean {
    return !!body &&
      (body.errorCode === MORNING_OVERTIME_ERROR_CODES.alreadyAnswered ||
      body.errorCode === MORNING_OVERTIME_ERROR_CODES.groupDisabled);
  }

  private showSubmitInfo(body: MorningOvertimeReadinessErrorBody | null): void {
    this.toastService.info(this.translate.instant(this.resolveSubmitErrorKey(body)));
  }

  private showSubmitError(body: MorningOvertimeReadinessErrorBody | null): void {
    var state = this.promptStateSubject.getValue();
    this.promptStateSubject.next({
      isVisible: state.isVisible,
      isLoading: false,
      isSubmitting: false,
      prompt: state.prompt,
      messageKey: this.resolveSubmitErrorKey(body),
      commentErrorKey: this.resolveCommentErrorKey(body)
    });
  }

  private resolveCommentErrorKey(body: MorningOvertimeReadinessErrorBody | null): string {
    if (body && body.errorCode === MORNING_OVERTIME_ERROR_CODES.commentTooLong) {
      return MORNING_OVERTIME_I18N_KEYS.commentTooLong;
    }
    return '';
  }

  private resolveSubmitErrorKey(body: MorningOvertimeReadinessErrorBody | null): string {
    if (!body) {
      return MORNING_OVERTIME_I18N_KEYS.submitError;
    }
    return this.mapErrorCodeToMessage(body.errorCode);
  }

  private mapErrorCodeToMessage(errorCode: string): string {
    switch (errorCode) {
      case MORNING_OVERTIME_ERROR_CODES.alreadyAnswered:
        return MORNING_OVERTIME_I18N_KEYS.alreadyAnswered;
      case MORNING_OVERTIME_ERROR_CODES.afterCutoff:
        return MORNING_OVERTIME_I18N_KEYS.afterCutoff;
      case MORNING_OVERTIME_ERROR_CODES.groupDisabled:
        return MORNING_OVERTIME_I18N_KEYS.groupDisabled;
      case MORNING_OVERTIME_ERROR_CODES.dailyTestingRequired:
        return MORNING_OVERTIME_I18N_KEYS.dailyTestingRequired;
      case MORNING_OVERTIME_ERROR_CODES.noMainGroup:
        return MORNING_OVERTIME_I18N_KEYS.noMainGroup;
      case MORNING_OVERTIME_ERROR_CODES.userNotActive:
        return MORNING_OVERTIME_I18N_KEYS.userNotActive;
      case MORNING_OVERTIME_ERROR_CODES.groupPlanfixIdMissing:
        return MORNING_OVERTIME_I18N_KEYS.groupPlanfixIdMissing;
      case MORNING_OVERTIME_ERROR_CODES.roleHead:
      case MORNING_OVERTIME_ERROR_CODES.roleNotEligible:
        return MORNING_OVERTIME_I18N_KEYS.accessDenied;
      default:
        return MORNING_OVERTIME_I18N_KEYS.submitError;
    }
  }

  private hidePrompt(): void {
    this.nextPromptStateRequestId();
    this.clearCutoffTimer();
    this.promptStateSubject.next(MORNING_OVERTIME_HIDDEN_PROMPT_STATE);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/morning-overtime-readiness/services/morning-overtime-readiness-facade.service.ts